// Affiche les erreurs d'écriture Supabase remontées par les providers métier.
// Aucune opération échouée ne doit rester silencieuse : ce composant garantit
// qu'un message compréhensible apparaît lorsque l'enregistrement serveur échoue.

import { useEffect, useRef, useState } from "react";
import { DATA_ERROR_EVENT, type DataErrorDetail, type DataNoticeTone } from "@/store/shared/dataErrors";

interface ToastItem extends DataErrorDetail {
  id: number;
}

const AUTO_DISMISS_MS = 6000;

const TONES: Record<DataNoticeTone, { wrap: string; iconBg: string; icon: string; label: string; glyph: string }> = {
  error: {
    wrap: "border-accent-300",
    iconBg: "bg-accent-100 text-accent-700",
    label: "text-accent-700",
    glyph: "ri-error-warning-line",
  },
  success: {
    wrap: "border-secondary-300",
    iconBg: "bg-secondary-100 text-secondary-700",
    label: "text-secondary-700",
    glyph: "ri-checkbox-circle-line",
  },
};

export default function DataErrorToaster() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const counterRef = useRef(0);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<DataErrorDetail>).detail;
      if (!detail) return;
      counterRef.current += 1;
      const id = counterRef.current;
      setToasts((prev) => [...prev, { ...detail, id }]);
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((item) => item.id !== id));
      }, AUTO_DISMISS_MS);
    };
    window.addEventListener(DATA_ERROR_EVENT, handler);
    return () => window.removeEventListener(DATA_ERROR_EVENT, handler);
  }, []);

  const dismiss = (id: number) => setToasts((prev) => prev.filter((item) => item.id !== id));

  if (toasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-[min(92vw,26rem)] flex-col gap-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="alert"
          className={`pointer-events-auto flex items-start gap-3 rounded-lg border bg-background-50 p-3.5 shadow-sm ${
            TONES[toast.tone ?? "error"].wrap
          }`}
        >
          <span
            className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${
              TONES[toast.tone ?? "error"].iconBg
            }`}
          >
            <i className={`${TONES[toast.tone ?? "error"].glyph} text-lg`} aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <p
              className={`text-xs font-label font-semibold uppercase tracking-wide ${
                TONES[toast.tone ?? "error"].label
              }`}
            >
              {toast.scope}
            </p>
            <p className="mt-0.5 text-sm leading-snug text-foreground-900">{toast.message}</p>
          </div>
          <button
            type="button"
            onClick={() => dismiss(toast.id)}
            aria-label="Fermer"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-800 cursor-pointer"
          >
            <i className="ri-close-line text-base" aria-hidden="true" />
          </button>
        </div>
      ))}
    </div>
  );
}